/**
 * AI Chatbot Module — Centralized API service
 * Backend endpoints under /api/ai (chatbot config) and /api/kb (knowledge base).
 */
import axiosConfig from '../../../config/axiosConfig';

/* ──────────────── helpers ──────────────── */
const get  = (path, params) => axiosConfig.get(path, { params });
const post = (path, body)   => axiosConfig.post(path, body);
const put  = (path, body)   => axiosConfig.put(path, body);
const del  = (path)         => axiosConfig.delete(path);

/* ──────────────── AI Chatbot Config APIs ──────────────── */
export const aiConfigApi = {
  listSkills:    ()      => get('/ai/skills'),
  getConfig:     ()      => get('/ai/config'),
  updateConfig:  (data)  => put('/ai/config', data),
  getUsage:      ()      => get('/ai/usage'),
  listLogs:      (params)=> get('/ai/logs', params),

  // Per-campaign AI configuration overrides
  getCampaignAIConfig:    (campaignId)        => get(`/ai/campaigns/${campaignId}/ai-config`),
  upsertCampaignAIConfig: (campaignId, data)  => put(`/ai/campaigns/${campaignId}/ai-config`, data),
  deleteCampaignAIConfig: (campaignId)        => del(`/ai/campaigns/${campaignId}/ai-config`),

  // Routing pipeline test
  testRouting:        (message, phone)    => post('/ai/routing/test', { message, phone }),
};

/* ──────────────── Topup Plans (Phase 10 — Token Purchase) ──────────────── */
export const topupApi = {
  // Public list (anyone can see active plans)
  listPlans: () => axiosConfig.get('/public/topup-plans'),
  // Authenticated topup-plan-based Razorpay order creation
  createOrder: ({ topupPlanId, amount, currency = 'INR' }) =>
    axiosConfig.post('/payment/order', { topupPlanId, amount, currency }),
  verifyPayment: (payload) => axiosConfig.post('/payment/verify', payload),
};

/* ──────────────── Super-Admin AI Console (Phase 11) ──────────────── */
export const aiSuperAdminApi = {
  platformOverview: (windowDays = 30) => get('/superadmin/ai/platform-overview', { window: windowDays }),
  listCustomers:    (windowDays = 30) => get('/superadmin/ai/customers', { window: windowDays }),
  customerDetails:  (companyId, windowDays = 30) =>
    get(`/superadmin/ai/customers/${companyId}`, { window: windowDays }),
  setCustomerBudget:(companyId, body) => put(`/superadmin/ai/customers/${companyId}/budget`, body),
  topCompanies:     (windowDays = 30, limit = 20) =>
    get('/superadmin/ai/top-companies', { window: windowDays, limit }),
  marketplaceList:  ()      => get('/superadmin/ai/skills/marketplace'),
  marketplaceUpsert:(data)  => post('/superadmin/ai/skills/marketplace', data),
  marketplaceDelete:(slug)  => del(`/superadmin/ai/skills/marketplace/${slug}`),
};

/* ──────────────── Knowledge Base APIs ──────────────── */
export const kbApi = {
  list:          ()                        => get('/kb/documents'),
  createText:    (title, text)             => post('/kb/documents/text', { title, text }),
  createUrl:     (title, url)              => post('/kb/documents/url', { title, url }),
  uploadPdf:     (formData)                => axiosConfig.post('/kb/documents/pdf', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  reindex:       (id)                      => post(`/kb/documents/${id}/reindex`),
  remove:        (id)                      => del(`/kb/documents/${id}`),
  getById:       (id)                      => get(`/kb/documents/${id}`),
  listTags:      ()                        => get('/kb/tags'),

  // Skill docs — fallback RAG tier (uploaded MD or PDF)
  listSkillDocs:   ()           => get('/kb/skill-docs'),
  uploadSkillDoc:  (formData)   => axiosConfig.post('/kb/skill-docs', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  removeSkillDoc:  (id)         => del(`/kb/documents/${id}`),
  reindexSkillDoc: (id)         => post(`/kb/documents/${id}/reindex`),
};
